import { Component, OnInit } from "@angular/core";
import { GenderPrice } from "../shared/api/model/gender-price";
import { GlobalService } from "../shared/global.service";
import { Booking } from "../shared/api/model/booking";
import { Subscription } from "rxjs";
import { WidgetService } from "../shared/api/widget/widget.service";

import { Product } from "../shared/api/model/product";
import { Event } from "../shared/api/model/event";
import { Router } from "@angular/router";
import { BookingCalculation } from "../shared/api/model/booking-calculation";
import { StaticUtils } from "../shared/utils/static-utils";
import { CartService } from "../shared/cart/cart.service";
import { ToasterService } from "../shared/atoms/toaster/toaster.service";
import moment from "moment";

@Component({
  selector: "app-pax",
  templateUrl: "./pax.component.html",
  styleUrls: ["./pax.component.scss"],
})
export class PaxComponent implements OnInit {
  utils = StaticUtils;
  eventGenderPrices: GenderPrice[];
  calculatedPriceSub: Subscription;
  calculatingPrice = false;
  product: Product;
  event: Event;
  _isToRejectBookingMinMax: boolean = false;
  _reachedMinimumPax: boolean = false;
  _reachedMaximumPax: boolean = false;
  _hasMinLotation: boolean = false;
  _hasGenderPriceDepsActive: boolean = false;
  _reachedLimitOfAvailability: boolean = false;
  _sumOfGenderPriceQuantityToBook: number | null = null;
  _newQuantityAvailable: number | null = null;
  moment = moment;

  constructor(
    private _router: Router,
    private _globalService: GlobalService,
    private _widgetService: WidgetService,
    private _toasterService: ToasterService,
    private _cartService: CartService,
  ) {}

  ngOnInit() {
    this.event = this._globalService.event;
    this.product = this._globalService.product;
    this.eventGenderPrices = this._globalService.eventGenderPrices;

    for (let i = 0; i < this.eventGenderPrices.length; i++) {
      if (this.eventGenderPrices[i].audienceTypeOccupancy >= 2) {
        this.eventGenderPrices[i].maxAvailabilityForCurrentAudienceType =
          Math.floor(
            this.event.maximumLotation /
              this.eventGenderPrices[i].audienceTypeOccupancy
          );
      } else {
        this.eventGenderPrices[i].maxAvailabilityForCurrentAudienceType =
          this.event.maximumLotation; // start with event.maximumLotation. Will be changed later.
      }
    }

    this._newQuantityAvailable = this.event.quantityAvailable - this._globalService.totalPax();

    if (this._globalService.booking) {
      this._globalService.booking.bookingComponents = undefined;
      this.updatePrice();
    }
  }

  public async goToSummary(): Promise<void> {
    if (
      (this.event.widgetHaveAccessExtras && this._globalService.hasExtras()) ||
      (this.event.widgetHaveAccessPickups && this._globalService.hasTransport())
    ) {
      await this._router.navigate(["../add/"], {
        queryParamsHandling: "merge",
      });
      this._globalService.selectedTransportComponent = undefined
    } else if (this.event.bookingQuestionIsActive) {
      await this._router.navigate(["../question/"], {
        queryParamsHandling: "merge",
      });
    } else {
      try {
        this._globalService.loading = true;
        await this._cartService.addBookingToCart(this.booking);
        this._globalService.clearBookingProductAndEvent();
        (await this._globalService.skip_my_cart) === true
          ? this._router.navigate(["../summary/"], {
              queryParamsHandling: "merge",
            })
          : this._router.navigate(["../cart/"], {
              queryParamsHandling: "merge",
            });
      } catch (e) {
        this._toasterService.showError(e);
      } finally {
        this._globalService.loading = false;
      }
    }
  }

  public reachedMaximumPaxAndIsBlocking = (maximumPaxPerBooking: number, rejectBookingBasedOnMinMax:boolean, eventGenderPrices: any): boolean => {
    this._sumOfGenderPriceQuantityToBook = eventGenderPrices
      .filter((gp: any) => gp.audienceType !== null) 
      .map((gp: any) => gp.quantityToBook)
      .reduce((prev: number, next: number) => prev + next, 0);

    if (!rejectBookingBasedOnMinMax) {
      return false;
    }
    
    if (maximumPaxPerBooking !== 0 && maximumPaxPerBooking === this._sumOfGenderPriceQuantityToBook) {
        return true;
    }
    
    return false;
  }

  public genderPricesDependenciesMessage(genderPrices: any) {
    let validationMessages: Array<string> = [];
    
    if (!genderPrices) {
        return [];
    }

    genderPrices.forEach((price: {quantityToBook: number; genderPriceDependencies: any[]; audienceTypeDescription: any; dependencySettings: any}) => {
        if (price.quantityToBook <= 0) {
            return;
        }

        if (!price.genderPriceDependencies || price.genderPriceDependencies.length === 0) {
            return;
        }

        const dependencyType = price.dependencySettings && price.dependencySettings.dependencyType;
        const deps = price.genderPriceDependencies;

        // Dependency Type | 1 - OR | 0 - AND
        if (dependencyType === 1) {
            const hasSomeDependentPrice = deps.some(dep => {
                    const dependentPrice = genderPrices.find((p: {
                        audienceType: any;
                        audienceTypeGroup: any;
                    }) =>
                        p.audienceType === dep.genderPriceDependsOn.audienceType &&
                        p.audienceTypeGroup === dep.genderPriceDependsOn.audienceTypeGroup
                    );

                    return dependentPrice && dependentPrice.quantityToBook >= dep.quantity;
                }
            );

            if (!hasSomeDependentPrice) {
                const allDepsJoined = this.buildDepsList(deps, ' OR ', genderPrices);
                const errorMessage = `The ${price.audienceTypeDescription} requires at least ${allDepsJoined} in the reservation.`;

                validationMessages.push(errorMessage);
            }
        } else {
            // Filters unmet dependencies
            const unmetDeps = deps.filter(dep => {
              const dependentPrice = genderPrices.find(p =>
                p.audienceType === dep.genderPriceDependsOn.audienceType &&
                p.audienceTypeGroup === dep.genderPriceDependsOn.audienceTypeGroup
              );
              
              return !dependentPrice || dependentPrice.quantityToBook < dep.quantity;
            });

            if (unmetDeps.length > 0) {
              const allDepsJoined = this.buildDepsList(unmetDeps, ' AND ', genderPrices);
              const errorMessage = `The ${price.audienceTypeDescription} requires at least ${allDepsJoined} in the reservation.`;

              validationMessages.push(errorMessage);
            }
        }
    });

    this._hasGenderPriceDepsActive = validationMessages.length > 0;

    return validationMessages;
  }

  buildDepsList(deps: any, operator: string, genderPrices: any) {
      if (!Array.isArray(deps)) {
          return [];
      }

      // For each dependency, generate only the part: '1 ADULT' and joined them
      return deps.map(dep => {
          const dependentDescription = this.getAudienceTypeDescription(
              genderPrices,
              dep.genderPriceDependsOn.audienceType,
              dep.genderPriceDependsOn.audienceTypeGroup
          );

          return `${dep.quantity} ${dependentDescription}`;
      }).join(operator);
  }

  // Find the description corresponding to audienceType
  getAudienceTypeDescription(genderPrices: any[], audienceType: any, audienceTypeGroup: any) {
    const price = genderPrices.find((p: { audienceType: any; audienceTypeGroup: any; }) => p.audienceType === audienceType && p.audienceTypeGroup === audienceTypeGroup);

    return price ? price.audienceTypeDescription : "Unknown Group";
  }

  public updateQuantity(index: number, value: number): void {
    if (this.calculatedPriceSub) {
      this.calculatedPriceSub.unsubscribe();
    }
    if (this.isGenderPriceVisible(this.eventGenderPrices[index])) {
      if (this.eventGenderPrices[index]) {
        this.eventGenderPrices[index].quantityToBook = value;
        this._newQuantityAvailable = this.event.quantityAvailable - this._globalService.totalPax();

        for (let i = 0; i < this.eventGenderPrices.length; i++) {
          if (i !== index) {
            if (this.eventGenderPrices[i].audienceTypeOccupancy >= 2) {
              // 17 / 2
              this.eventGenderPrices[i].maxAvailabilityForCurrentAudienceType =
                Math.floor(
                  this._newQuantityAvailable /
                    this.eventGenderPrices[i].audienceTypeOccupancy
                ) + this.eventGenderPrices[i].quantityToBook;
            } else {
              this.eventGenderPrices[i].maxAvailabilityForCurrentAudienceType =
                this._newQuantityAvailable +
                this.eventGenderPrices[i].quantityToBook;
            }
          }
        }
      }

      this._globalService.eventGenderPrices = this.eventGenderPrices;

      const booking = new Booking();
      booking.bookingGenderPrices = this.eventGenderPrices;
      booking.event = this._globalService.event;
      this._globalService.booking = booking;
      this.updatePrice();
    }
  }

  public updatePrice(): void {
    this.calculatingPrice = true;
    this.calculatedPriceSub = this._widgetService.calculatePrice(this.booking)
    .subscribe(
      (bookingCalculation: BookingCalculation) => {
        // Success
        this._globalService.calculatedPrice = bookingCalculation;
        this._globalService.eventGenderPrices = bookingCalculation.bookingGenderPrices;
        this.eventGenderPrices = this._globalService.eventGenderPrices;

        if (this._globalService.booking) {
          this._globalService.booking.calculationsGUID = bookingCalculation.calculationsGUID;
        }
      },
      (error: any) => {
        // Error
        this.calculatingPrice = false;
      },
      () => {
        // Finalized
        this.calculatingPrice = false;
      }
    );
  }

  public hasMoreThanOnePricePerGroup(): boolean {
    const passengerSettings = this.event.passengerSettings;
    const eventGenderPrices = this.eventGenderPrices;
  
    if (!passengerSettings || !eventGenderPrices) return false;
  
    const hasConfig = passengerSettings.configurations && passengerSettings.configurations.length > 1;
    const hasTiered = passengerSettings.tieredConfigurations && Object.values(passengerSettings.tieredConfigurations).some(arr => arr && arr.length > 1);
    const isPricePerGroup = eventGenderPrices.some(p => p.genderPriceType === 3);
  
    return (hasConfig || hasTiered) && isPricePerGroup;
  }

  public getPriceGroupOldPrice(genderPrice: GenderPrice): boolean | number {
    if (genderPrice.genderPriceType !== 3 || 
        !this._globalService || 
        !this._globalService.eventGenderPrices ||
        !this.event.passengerSettings || 
        (!this.event.passengerSettings.configurations && !this.event.passengerSettings.tieredConfigurations) ||
        genderPrice.genderPriceType === 3 && this.event.passengerSettings.format === 0) {
        
        return false;
    }

    const updatedPricePerson = this._globalService.eventGenderPrices.find(b => b.audienceType === genderPrice.audienceType);
    const prices = this.event.passengerSettings.format === 2 ? this.event.passengerSettings.tieredConfigurations[updatedPricePerson.hashedAudience] : this.event.passengerSettings.configurations;
    const indexActualPrice = prices.findIndex(a => a.price === updatedPricePerson.grossprice);
    let result: any;
    
    if (indexActualPrice === 0) {
        return false;
    }

    const index = indexActualPrice === -1 ? prices.length - 1 : indexActualPrice - 1;
    result = prices[index].price;

    return result;
  }

  public getGenderPriceLabel(genderPrice: GenderPrice): string {
    return genderPrice.audienceTypeDescription || "Tickets";
  }

  public isGenderPriceVisible(genderPrice: GenderPrice): boolean {
    return (
      genderPrice.genderPriceType !== 1 ||
      (genderPrice.genderPriceType === 1 && genderPrice.isExcludedFromBilling)
    );
  }

  public hasAvailableQuantity(event: Event, atLeastOne = false): boolean {
    return (
      this.isUnlimited(event) ||
      (atLeastOne
        ? this.totalQuantity < event.quantityAvailable
        : this.totalQuantity <= event.quantityAvailable)
    );
  }

  public isUnlimited(event: Event): boolean {
    return (
      event.availabilityType === 1 ||
      event.productAvailabilityTypeDescription === "Unlimited" ||
      event.hasLotation === false ||
      event.quantityAvailable === -1 ||
      event.maximumLotation === -1
    );
  }
  public isToRejectBookingMinMax( rejectBookingBasedOnMinMax:boolean): void {
    this._isToRejectBookingMinMax = rejectBookingBasedOnMinMax
  }
  
  public reachedMinimumPax = (minimumPaxPerBooking: number, eventGenderPrices: any, i: number): void => {
    this._sumOfGenderPriceQuantityToBook = eventGenderPrices
      .filter((gp: any) => gp.audienceType !== null) 
      .map((gp: any) => gp.quantityToBook)
      .reduce((prev: number, next: number) => prev + next, 0);

      this._reachedMinimumPax = minimumPaxPerBooking !== 0 && 
        minimumPaxPerBooking > this._sumOfGenderPriceQuantityToBook;
  };
  public reachedMaximumPax = (
    maximumPaxPerBooking: number,
    rejectBookingBasedOnMinMax:boolean,
    eventGenderPrices: any,
    i: number
  ): boolean => {
    this._sumOfGenderPriceQuantityToBook = eventGenderPrices
      .filter((gp: any) => gp.audienceType !== null) 
      .map((gp: any) => gp.quantityToBook)
      .reduce((prev: number, next: number) => prev + next, 0);
      if(rejectBookingBasedOnMinMax){
        if (maximumPaxPerBooking === this._sumOfGenderPriceQuantityToBook && maximumPaxPerBooking !== 0) {
          this._reachedMaximumPax= true;
        } else {
          this._reachedMaximumPax = false;
        }
      } else if (maximumPaxPerBooking < this._sumOfGenderPriceQuantityToBook && maximumPaxPerBooking !== 0) {
        this._reachedMaximumPax= true;
      } else {
        this._reachedMaximumPax = false;
      }

    return this._reachedMaximumPax;
  }
  public reachedLimitOfAvailability = (quantityAvailable: number, eventGenderPrices: any, i: number): void => {
    this._sumOfGenderPriceQuantityToBook = eventGenderPrices
      .filter((gp: any) => gp.audienceType !== null) // discard those without audience type defined
      .map((gp: any) => gp.quantityToBook)
      .reduce((prev: number, next: number) => prev + next, 0);

      this._reachedLimitOfAvailability = quantityAvailable === this._sumOfGenderPriceQuantityToBook || 
        eventGenderPrices[i].audienceTypeOccupancy > this._newQuantityAvailable;
  };

  public hasMinLotation = (event: any,): any => {
    const { minimumLotation, quantityBooked } = event;
    
    const sumOfGenderPriceQuantityToBook = event.eventGenderPrices
      .filter((gp: any) => gp.audienceType !== null)
      .map((gp: any) => gp.quantityToBook)
      .reduce((prev: number, next: number) => prev + next, 0);

    // Event minimuns validation
    this._hasMinLotation = minimumLotation !== 0 &&
      minimumLotation > (sumOfGenderPriceQuantityToBook + quantityBooked);
  };

  get booking(): Booking {
    return this._globalService.booking;
  }

  get totalPrice(): string {
    return StaticUtils.convertValueToCurrency(
      this._globalService.calculatedPrice
        ? this._globalService.calculatedPrice.grandTotal
        : 0
    );
  }

  get totalQuantity(): number {
    let result = 0;
    for (const eventGenderPrice of this.eventGenderPrices) {
      if (this.isGenderPriceVisible(eventGenderPrice)) {
        if (eventGenderPrice.audienceTypeOccupancy >= 2) {
          result +=
            eventGenderPrice.audienceTypeOccupancy *
            eventGenderPrice.quantityToBook;
        } else {
          result += eventGenderPrice.quantityToBook;
        }
      }
    }
    return result;
  }

  get newQuantityAvailable(): number {
    return this._newQuantityAvailable;
  }

  get hasLotation(): boolean {
    return this.event.hasLotation;
  }

  get sumOfGenderPriceQuantityToBook(): number {
    return this._sumOfGenderPriceQuantityToBook;
  }

  get hasAtLeastOneBook(): boolean {
    return this.eventGenderPrices.some(price => price.quantityToBook > 0);
  }

  public isToHideDate(): boolean {
    return this._globalService.hide_date;
  }
}
